import { ReactElement } from 'react'
import { buildErrorMessage } from '@constants'
import { AxiosError } from 'axios'
import { Button } from '@mui/material'
import type { OrganisationDTO } from '@api/fastAPI.schemas'
import {
  getGetOrganisationsQueryKey,
  useUpdateOrganisation,
} from '@api/user-management/user-management'
import { useQueryClient } from '@tanstack/react-query'
import { SaveButton } from '@ui/actions'
import { DialogBase } from '@ui/feedback'
import { Form2 } from '@ui/forms'
import { useSnackbar } from '@ui/snackbar'
import OrganisationFormFields from '../OrganisationFormFields'
import {
  OrganisationFormValues,
  organisationValidationSchema,
} from '../OrganisationFormFields/schema'

interface Props {
  organisation: OrganisationDTO
  onClose: () => void
}

const FORM_ID = 'edit-organisation-form'

const EditOrganisationDialog = ({ organisation, onClose }: Props): ReactElement => {
  const { enqueueSnackbar } = useSnackbar()
  const queryClient = useQueryClient()

  const { mutate: updateOrganisation, isLoading } = useUpdateOrganisation<AxiosError, unknown>({
    mutation: {
      meta: {
        title: 'Organisation bearbeiten',
        description: 'Ändert Name und Adresse einer Organisation',
      },
      onSuccess: async () => {
        await queryClient.invalidateQueries(getGetOrganisationsQueryKey())
        enqueueSnackbar('Organisation erfolgreich aktualisiert', { variant: 'success' })
        onClose()
      },
      onError: error => {
        enqueueSnackbar(buildErrorMessage(error, 'Fehler beim Aktualisieren der Organisation'), {
          variant: 'error',
        })
      },
    },
  })

  return (
    <DialogBase
      open
      onClose={onClose}
      title="Organisation bearbeiten"
      dialogProps={{ maxWidth: 'sm', fullWidth: true }}
    >
      <Form2<OrganisationFormValues>
        id={FORM_ID}
        defaultValues={{
          name: organisation.name || '',
          street: organisation.street || '',
          zipCode: organisation.zip_code || '',
          city: organisation.city || '',
        }}
        validationSchema={organisationValidationSchema}
        onSubmit={values =>
          updateOrganisation({
            organisationGuid: organisation.guid,
            data: {
              name: values.name.trim(),
              street: values.street.trim(),
              zip_code: values.zipCode.trim(),
              city: values.city.trim(),
            },
          })
        }
      >
        <OrganisationFormFields />
      </Form2>
      <>
        <Button size="small" onClick={onClose}>
          Abbrechen
        </Button>
        <SaveButton
          size="small"
          loading={isLoading}
          form={FORM_ID}
          type="submit"
          data-cy="btn-save-edit-organisation"
        >
          Speichern
        </SaveButton>
      </>
    </DialogBase>
  )
}

export default EditOrganisationDialog
